<script setup>
import { computed, defineEmits, defineProps, onMounted, ref, watch } from "vue";
import { TrashIcon } from "@heroicons/vue/24/solid";
import apiClient from "@/apiClient";
import CustomerContactSelect from "@/components/CustomerContactSelect.vue";
import QuoteAddressManager from "@/components/QuoteAddressManager.vue";

const props = defineProps({
  risk: { type: Object, required: true },
  entries: { type: Object, required: true },
  customer: { type: Object, required: false },
  recipientContact: { type: Object, required: false },
  editable: { type: Boolean, required: false },
});

const emit = defineEmits([
  "update:risk",
  "update:entries",
  "update:customer",
  "update:recipient-contact",
]);

const updateFields = (updates) => {
  emit("update:risk", { ...(props.risk ?? {}), ...updates });
};

const updateField = (field, value) => {
  updateFields({ [field]: value });
};

const inputClass = "w-full p-2 rounded border bg-white dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100";
const selectClass = inputClass;
const dateEntryKeyField = "__quoteEventBasicsKey";
let nextDateEntryKey = 1;

const compareDateEntries = (a, b) =>
  String(a?.date ?? "").localeCompare(String(b?.date ?? "")) ||
  String(a?.start_time ?? "").localeCompare(String(b?.start_time ?? "")) ||
  String(a?.finish_time ?? "").localeCompare(String(b?.finish_time ?? ""));
const sortDateEntries = (entries) => [...entries].sort(compareDateEntries);
const ensureDateEntryKey = (entry) => {
  if (!entry || typeof entry !== "object") return null;
  if (!entry[dateEntryKeyField]) {
    entry[dateEntryKeyField] = `local-date-${nextDateEntryKey}`;
    nextDateEntryKey += 1;
  }

  return entry[dateEntryKeyField];
};
const cloneEntries = (val) => {
  const cloned = JSON.parse(JSON.stringify(val ?? { dates: [] }));
  if (Array.isArray(cloned.dates)) {
    cloned.dates.forEach(ensureDateEntryKey);
    cloned.dates = sortDateEntries(cloned.dates);
  }

  return cloned;
};
const localEntries = ref(cloneEntries(props.entries));
const dateEntryKey = (entry) => {
  if (entry?.id !== undefined && entry?.id !== null) return `date-${entry.id}`;
  if (!entry || typeof entry !== "object") return `date-${String(entry ?? "")}`;

  return ensureDateEntryKey(entry);
};
const dateRows = computed(() => (Array.isArray(localEntries.value?.dates) ? localEntries.value.dates : []));
const eventTypes = ref([]);
const normalizeAddressKey = (value) => (value ?? "")
  .toString()
  .trim()
  .toLowerCase()
  .replace(/\s+/g, " ");
const normalizeAddressText = (value) => (value ?? "")
  .toString()
  .trim()
  .replace(/\s+/g, " ");
const mergeVenueAddresses = (...collections) => {
  const merged = [];
  const seen = new Set();
  const addAddress = (value) => {
    const normalized = normalizeAddressText(value);
    if (!normalized) return;

    const key = normalizeAddressKey(normalized);
    if (!key || seen.has(key)) return;

    seen.add(key);
    merged.push(normalized);
  };

  collections.forEach((collection) => {
    if (Array.isArray(collection)) {
      collection.forEach(addAddress);
      return;
    }
    addAddress(collection);
  });

  return merged;
};
const riskVenueAddresses = computed(() => mergeVenueAddresses(props.risk?.venue_addresses));
const addressOptions = computed(() => {
  const options = [];
  const seen = new Set();

  const addAddress = (value) => {
    const trimmed = (value ?? "").toString().trim();
    if (!trimmed) return;

    const key = normalizeAddressKey(trimmed);
    if (!key || seen.has(key)) return;

    seen.add(key);
    options.push({
      id: `venue-${seen.size}`,
      data: trimmed,
    });
  };

  const dates = localEntries.value?.dates ?? [];
  dates.forEach((entry) => {
    addAddress(entry?.location_address);
  });

  addAddress(props.risk?.address);
  riskVenueAddresses.value.forEach((venueAddress) => {
    addAddress(venueAddress);
  });

  return options;
});
const hasAddresses = computed(() => addressOptions.value.length > 0);
const fallbackVenueAddress = computed(() => {
  const riskAddress = normalizeAddressText(props.risk?.address);
  if (riskAddress) return riskAddress;

  return riskVenueAddresses.value[0] ?? null;
});
const firstNonEmpty = (...values) => {
  for (const value of values) {
    const normalized = String(value ?? "").trim();
    if (normalized !== "") return normalized;
  }

  return null;
};
const selectedCustomerName = computed(
  () => firstNonEmpty(props.risk?.customer_name, props.recipientContact?.name),
);
const selectedCustomerEmail = computed(
  () => firstNonEmpty(props.risk?.customer_email, props.recipientContact?.email),
);

watch(
  () => props.entries,
  (val) => {
    localEntries.value = cloneEntries(val);
  },
  { deep: true, immediate: true },
);

const commitEntries = () => {
  if (Array.isArray(localEntries.value?.dates)) {
    localEntries.value.dates.forEach(ensureDateEntryKey);
    localEntries.value.dates = sortDateEntries(localEntries.value.dates);
  }

  emit("update:entries", cloneEntries(localEntries.value));
};

const isCustomAddress = (entry) => {
  const address = entry?.location_address;
  if (!address) return false;
  return !addressOptions.value.some((option) => option.data === address);
};

const updateEntryAddress = (entry, value) => {
  const nextAddress = normalizeAddressText(value);
  entry.location_address = nextAddress || null;
  entry.location_name = null;
  commitEntries();
};

const addDateRow = () => {
  if (!localEntries.value.dates) localEntries.value.dates = [];
  const defaultAddress = (props.risk?.address ?? "").toString().trim()
    || (addressOptions.value[0]?.data ?? null);
  localEntries.value.dates.push({
    date: new Date().toISOString().slice(0, 10),
    start_time: "08:00:00",
    finish_time: "18:00:00",
    location_name: null,
    location_address: defaultAddress,
    resources: [],
  });
  commitEntries();
};

const removeDateRow = (entry) => {
  localEntries.value.dates = localEntries.value.dates.filter((d) => d !== entry);
  commitEntries();
};

watch(
  () => fallbackVenueAddress.value,
  (fallbackAddress) => {
    const trimmedFallback = normalizeAddressText(fallbackAddress);
    if (!trimmedFallback) return;

    const dates = localEntries.value?.dates ?? [];
    let changed = false;
    dates.forEach((entry) => {
      if (!entry?.location_address) {
        entry.location_address = trimmedFallback;
        changed = true;
      }
    });

    if (changed) {
      commitEntries();
    }
  },
  { immediate: true },
);

onMounted(async () => {
  try {
    const response = await apiClient.get("/api/event-types");
    eventTypes.value = response.data ?? [];
  } catch (error) {
    console.error(error);
    eventTypes.value = [];
  }
});

const selectCustomer = (customer) => {
  emit("update:customer", customer);
  updateFields({
    customer_name: customer?.company ?? customer?.contact?.name ?? null,
    customer_email: customer?.contact?.email ?? null,
    customer_phone: customer?.contact?.phone ?? null,
  });
};

const selectContact = (contact) => {
  emit("update:recipient-contact", contact);
  updateFields({
    customer_name: contact?.name ?? null,
    customer_email: contact?.email ?? null,
    customer_phone: contact?.phone ?? null,
  });
};

const addVenueAddress = (address) => {
  const trimmed = normalizeAddressText(address);
  if (!trimmed) return;

  const updates = {};
  const nextVenueAddresses = mergeVenueAddresses(riskVenueAddresses.value, trimmed);
  if (nextVenueAddresses.length !== riskVenueAddresses.value.length) {
    updates.venue_addresses = nextVenueAddresses;
  }

  const currentAddress = normalizeAddressText(props.risk?.address);
  const dates = localEntries.value?.dates ?? [];
  const currentAddressKey = normalizeAddressKey(currentAddress);
  const dateAddressKeys = Array.from(
    new Set(
      dates
        .map((entry) => normalizeAddressKey(entry?.location_address))
        .filter(Boolean),
    ),
  );
  const replaceableAddressKey =
    currentAddressKey ||
    (dateAddressKeys.length === 1 ? dateAddressKeys[0] : "");
  const allDatesUseCurrentOrBlank =
    dates.length > 0 &&
    replaceableAddressKey &&
    dates.every((entry) => {
      const entryAddress = normalizeAddressText(entry?.location_address);
      return (
        !entryAddress ||
        normalizeAddressKey(entryAddress) === replaceableAddressKey
      );
    });
  const shouldPromotePrimaryAddress =
    !currentAddress || dates.length <= 1 || allDatesUseCurrentOrBlank;

  if (shouldPromotePrimaryAddress) {
    updates.address = trimmed;
  }

  if (Object.keys(updates).length) {
    updateFields(updates);
  }

  if (!dates.length) return;

  let changed = false;
  dates.forEach((entry) => {
    const entryAddress = normalizeAddressText(entry?.location_address);
    const shouldApplyToDate =
      dates.length === 1 ||
      !entryAddress ||
      (replaceableAddressKey &&
        normalizeAddressKey(entryAddress) === replaceableAddressKey);

    if (shouldApplyToDate && entry.location_address !== trimmed) {
      entry.location_address = trimmed;
      entry.location_name = null;
      changed = true;
    }
  });

  if (changed) {
    commitEntries();
  }
};
</script>

<template>
  <div class="space-y-4">
    <FormBox header="Contact">
      <CustomerContactSelect :editable="editable" :customer="props.customer"
        :customerName="selectedCustomerName" :contactEmail="selectedCustomerEmail"
        @update:customer="selectCustomer" @update:contact="selectContact" />
    </FormBox>

    <FormBox header="Event Basics">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-xs text-slate-500">Event Type</label>
          <select :class="selectClass" :disabled="!editable" :value="risk.event_type_id ?? ''"
            @change="updateField('event_type_id', Number($event.target.value) || null)">
            <option value="">Select type</option>
            <option v-for="option in eventTypes" :key="option.id" :value="option.id">{{ option.name }}</option>
          </select>
        </div>
        <div>
          <label class="block text-xs text-slate-500">Off-road 4x4 access required</label>
          <select :class="selectClass" :disabled="!editable" :value="risk.offroad_access"
            @change="updateField('offroad_access', $event.target.value === 'true')">
            <option :value="false">No</option>
            <option :value="true">Yes</option>
          </select>
        </div>
        <div>
          <label class="block text-xs text-slate-500">Expected Peak Attendance</label>
          <input :class="inputClass" type="number" :value="risk.expected_peak_attendance ?? ''"
            :disabled="!editable" @input="updateField('expected_peak_attendance', $event.target.value)" />
        </div>
        <div>
          <label class="block text-xs text-slate-500">Audience Profile</label>
          <input :class="inputClass" :value="risk.audience_profile ?? ''" :disabled="!editable"
            @input="updateField('audience_profile', $event.target.value)" />
        </div>
        <div>
          <label class="block text-xs text-slate-500">Highest Risk Activity</label>
          <input :class="inputClass" :value="risk.highest_risk_activity ?? ''" :disabled="!editable"
            @input="updateField('highest_risk_activity', $event.target.value)" />
        </div>
        <div>
          <label class="block text-xs text-slate-500">Alcohol consumption</label>
          <select :class="selectClass" :value="risk.alcohol" :disabled="!editable"
            @change="updateField('alcohol', $event.target.value === 'true')">
            <option :value="true">Yes</option>
            <option :value="false">No</option>
          </select>
        </div>
      </div>
    </FormBox>

    <FormBox header="Addresses">
      <QuoteAddressManager :addresses="addressOptions" :customer="props.customer" :editable="editable"
        @update:customer="customer => emit('update:customer', customer)"
        @add:address="addVenueAddress" />
    </FormBox>

    <FormBox header="Venue & Schedule">
      <div class="space-y-4">
        <div>
          <div class="flex flex-wrap items-center justify-between gap-2">
            <label class="block text-xs text-slate-500">Date & Time</label>
            <button class="btn accent" type="button" v-if="editable" @click.prevent="addDateRow">Add Date</button>
          </div>
          <p v-if="editable && !hasAddresses" class="mt-1 text-xs text-slate-500">
            Enter a venue address below or let ticket extraction populate it.
          </p>
          <TransitionGroup name="date-row" tag="div" class="relative mt-3 space-y-3">
            <div v-for="entry in dateRows" :key="dateEntryKey(entry)"
              class="rounded-lg border border-slate-200 dark:border-slate-700 p-3 bg-slate-50/70 dark:bg-slate-900/40">
              <div
                class="grid grid-cols-1 gap-2 md:grid-cols-[minmax(140px,1fr)_minmax(110px,0.6fr)_minmax(110px,0.6fr)_minmax(220px,1.5fr)_auto] md:items-end">
                <div>
                  <label class="block text-xs text-slate-500">Date</label>
                  <input :class="inputClass" type="date" :disabled="!editable" v-model="entry.date"
                    @blur="commitEntries" />
                </div>
                <div>
                  <label class="block text-xs text-slate-500">Start</label>
                  <input :class="inputClass" type="time" :disabled="!editable" v-model="entry.start_time"
                    @blur="commitEntries" />
                </div>
                <div>
                  <label class="block text-xs text-slate-500">Finish</label>
                  <input :class="inputClass" type="time" :disabled="!editable" v-model="entry.finish_time"
                    @blur="commitEntries" />
                </div>
                <div>
                  <label class="block text-xs text-slate-500">Address</label>
                  <select v-if="editable && hasAddresses" :class="selectClass"
                    :value="entry.location_address ?? ''"
                    @change="updateEntryAddress(entry, $event.target.value)">
                    <option value="">Select address</option>
                    <option v-if="isCustomAddress(entry)" :value="entry.location_address">
                      Current: {{ entry.location_address }}
                    </option>
                    <option v-for="address in addressOptions" :key="address.id" :value="address.data">
                      {{ address.data }}
                    </option>
                  </select>
                  <input v-else-if="editable" :class="inputClass" type="text"
                    :value="entry.location_address ?? ''"
                    placeholder="Enter venue address"
                    @input="updateEntryAddress(entry, $event.target.value)" />
                  <div v-else class="text-sm text-slate-600 dark:text-slate-300">
                    {{ entry.location_address || "Address not provided" }}
                  </div>
                </div>
                <div v-if="editable" class="flex md:justify-end">
                  <button class="btn danger" type="button" aria-label="Remove date" @click.prevent="removeDateRow(entry)">
                    <TrashIcon class="icon" />
                  </button>
                </div>
              </div>
            </div>
          </TransitionGroup>
        </div>
      </div>
    </FormBox>
  </div>
</template>

<style scoped>
.date-row-move,
.date-row-enter-active,
.date-row-leave-active {
  transition:
    transform 180ms ease,
    opacity 180ms ease;
}

.date-row-enter-from,
.date-row-leave-to {
  opacity: 0;
}

.date-row-leave-active {
  position: absolute;
  width: 100%;
}
</style>
